/**
 * Whether, how far and how often the rider may move the pickup AFTER a driver accepted the ride (D712, doc 28 gap G12 "Alterar"; owner 2026-10-24: "1 troca", and
 * owner 2026-10-24 again: no business rule of the change is a constant - every number of it is this governed key) - governed key `ride.pickupChange`.
 *
 * Shape: `{ enabled, maxDistanceMeters, maxChanges, allowedPhases, requoteToleranceMeters }`:
 *
 *  - `enabled`: the market lets the rider move the pickup once a driver is on the ride. ON where nothing governs;
 *  - `maxDistanceMeters`: how far (straight line, metres) the new pickup may sit from the pickup the driver ACCEPTED - the first one, so a ride with more than one
 *    change cannot walk the pickup away step by step ({@link PICKUP_CHANGE_BOUNDS}: 50 to 5 000). The rider's pin picker draws this very radius (never a second
 *    constant). A farther move is refused: the rider cancels and requests again (the normal cancellation rules apply);
 *  - `maxChanges`: how many times one ride's pickup may move ({@link PICKUP_CHANGE_BOUNDS}: 0 to 5; 0 means none);
 *  - `allowedPhases`: the ride statuses in which a change is accepted - a non-empty subset of `assigned` and `en_route` ({@link PICKUP_CHANGE_PHASES}). A ride whose
 *    driver has ARRIVED, or that is on board, never moves its pickup: those statuses are not representable here;
 *  - `requoteToleranceMeters`: a move up to this distance keeps the ride's fare without a new price; a farther move is priced again (the rider sees the new price
 *    before confirming when it differs). 0 to `maxDistanceMeters`.
 *
 * A ride rule, not a country-level legal, safety or financial constraint: country, state and market scope, OVERRIDABLE (the most specific version wins - a country at
 * 1 change / 300 m and one of its markets at 2 changes / 500 m: that market reads 2 / 500, every other market of the country 1 / 300), under four eyes on every
 * change, versioned, effective-dated and content-hashed. An UNSET market reads {@link DEFAULT_PICKUP_CHANGE} (on, 300 m, 1 change, assigned or en route, 25 m to
 * re-quote); a version the reader cannot verify or parse reads {@link STRICT_PICKUP_CHANGE} (no change) with `PICKUP_CHANGE_INVALID` - never a looser one.
 * Distances are metres (ADR-0007); no country, unit or language is named here.
 */
import { policySetting, resolvePolicy } from './policy.js';
import { settingSnapshotFields } from './setting-kernel.js';
import { SCOPE_REF_KIND, marketSettingContext, settingScopeRefIn, settingScopeRefValid } from './setting-context.js';
/** The governed key. */
export const PICKUP_CHANGE_KEY = 'ride.pickupChange';
/** The ride statuses a pickup change may be allowed in: a driver accepted and has not arrived. */
export const PICKUP_CHANGE_PHASES = ['assigned', 'en_route'];
/** The hard bounds the floor enforces (the migration mirrors them). */
export const PICKUP_CHANGE_BOUNDS = {
    maxDistanceMeters: { min: 50, max: 5_000 },
    maxChanges: { min: 0, max: 5 },
    requoteToleranceMeters: { min: 0, max: 5_000 },
};
/** What an ungoverned market reads: on, once per ride, within 300 m, while the driver is assigned or on the way; a move over 25 m is priced again. */
export const DEFAULT_PICKUP_CHANGE = { enabled: true, maxDistanceMeters: 300, maxChanges: 1, allowedPhases: ['assigned', 'en_route'], requoteToleranceMeters: 25 };
/** What an unreadable version reads: the strictest thing - no change. */
export const STRICT_PICKUP_CHANGE = { enabled: false, maxDistanceMeters: PICKUP_CHANGE_BOUNDS.maxDistanceMeters.min, maxChanges: 0, allowedPhases: ['assigned'], requoteToleranceMeters: 0 };
export const PICKUP_CHANGE_REASON_CODES = ['PICKUP_CHANGE_GOVERNED', 'PICKUP_CHANGE_DEFAULT', 'PICKUP_CHANGE_INVALID'];
/**
 * The stable codes of one decision about a ride's pickup change: why it is allowed or refused now, and how its fare was decided. The refusals are what the rider
 * app tells apart (too far: cancel and request again; already changed; the driver arrived; on board).
 */
export const PICKUP_CHANGE_DECISION_CODES = [
    'PICKUP_CHANGE_ALLOWED',
    'PICKUP_CHANGE_DISABLED',
    'PICKUP_CHANGE_PHASE_NOT_ALLOWED',
    'PICKUP_CHANGE_DRIVER_ARRIVED',
    'PICKUP_CHANGE_ON_BOARD',
    'PICKUP_CHANGE_LIMIT_REACHED',
    'PICKUP_CHANGE_TOO_FAR',
    'PICKUP_CHANGE_FARE_KEPT_WITHIN_TOLERANCE',
    'PICKUP_CHANGE_FARE_KEPT_NOT_REQUOTABLE',
    'PICKUP_CHANGE_REQUOTED',
];
const MARKET_ID = /^[a-z]{2}:[a-z0-9]+(?:-[a-z0-9]+)*$/;
const MEMBERS = ['enabled', 'maxDistanceMeters', 'maxChanges', 'allowedPhases', 'requoteToleranceMeters'];
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const intIn = (v, min, max) => typeof v === 'number' && Number.isSafeInteger(v) && v >= min && v <= max;
/** The stable issue of a value that is not a pickup-change value, or undefined when it is one. */
export function pickupChangeIssue(v) {
    if (!isObj(v))
        return 'NOT_AN_OBJECT';
    const keys = Object.keys(v);
    if (keys.some((k) => !MEMBERS.includes(k)) || MEMBERS.some((m) => !(m in v)))
        return 'UNKNOWN_OR_MISSING_MEMBER';
    if (typeof v.enabled !== 'boolean')
        return 'INVALID_ENABLED';
    if (!intIn(v.maxDistanceMeters, PICKUP_CHANGE_BOUNDS.maxDistanceMeters.min, PICKUP_CHANGE_BOUNDS.maxDistanceMeters.max))
        return 'INVALID_MAX_DISTANCE_METERS';
    if (!intIn(v.maxChanges, PICKUP_CHANGE_BOUNDS.maxChanges.min, PICKUP_CHANGE_BOUNDS.maxChanges.max))
        return 'INVALID_MAX_CHANGES';
    const phases = v.allowedPhases;
    if (!Array.isArray(phases) || phases.length === 0 || phases.some((p) => !PICKUP_CHANGE_PHASES.includes(p)) || new Set(phases).size !== phases.length)
        return 'INVALID_ALLOWED_PHASES';
    if (!intIn(v.requoteToleranceMeters, PICKUP_CHANGE_BOUNDS.requoteToleranceMeters.min, PICKUP_CHANGE_BOUNDS.requoteToleranceMeters.max))
        return 'INVALID_REQUOTE_TOLERANCE_METERS';
    if (v.requoteToleranceMeters > v.maxDistanceMeters)
        return 'REQUOTE_TOLERANCE_ABOVE_MAX_DISTANCE';
    return undefined;
}
export function validPickupChangeValue(v) {
    return pickupChangeIssue(v) === undefined;
}
/** `ride.pickupChange@<scope>:<ref>#v<n>`. */
export function pickupChangeVersionOf(scope, scopeRef, version) {
    return `${PICKUP_CHANGE_KEY}@${scope}:${scopeRef}#v${version}`;
}
const copyValue = (v) => ({
    enabled: v.enabled,
    maxDistanceMeters: v.maxDistanceMeters,
    maxChanges: v.maxChanges,
    allowedPhases: [...v.allowedPhases],
    requoteToleranceMeters: v.requoteToleranceMeters,
});
/** Build a valid version (country, state or market scope). Throws a RangeError on a malformed scope ref or value. */
export function pickupChangeSetting(input) {
    if (!settingScopeRefValid(input.scope, input.scopeRef)) {
        throw new RangeError(`a ${input.scope}-scope pickup change version needs a valid ${SCOPE_REF_KIND[input.scope]}, got ${input.scopeRef}`);
    }
    const issue = pickupChangeIssue(input.value);
    if (issue !== undefined)
        throw new RangeError(`invalid pickup change: ${issue}`);
    return policySetting({
        key: PICKUP_CHANGE_KEY,
        scope: input.scope,
        scopeRef: input.scopeRef,
        value: copyValue(input.value),
        version: input.version,
        effectiveFrom: input.effectiveFrom,
        ...(input.effectiveTo !== undefined ? { effectiveTo: input.effectiveTo } : {}),
        mode: 'OVERRIDABLE',
    });
}
/**
 * What `marketId` decides about a pickup change at `at`, over the GOVERNED reader's versions: the most specific of the country, the state and the market decides.
 * Nothing governing reads the default; a version that cannot be read or parsed reads the strictest (no change). `regionCode` is the market's registered state
 * (ADR-0124 - a market with none skips the layer).
 */
export function resolvePickupChange(settings, marketId, at, regionCode) {
    const strict = { marketId, value: STRICT_PICKUP_CHANGE, reasonCode: 'PICKUP_CHANGE_INVALID', policyVersion: `${PICKUP_CHANGE_KEY}@invalid` };
    if (!MARKET_ID.test(marketId))
        return strict;
    const ctx = marketSettingContext(marketId, regionCode);
    const country = ctx.country;
    const relevant = settings.filter((s) => s.key === PICKUP_CHANGE_KEY && (s.scope === 'country' || s.scope === 'region' || s.scope === 'market'));
    const resolution = resolvePolicy(relevant, ctx, PICKUP_CHANGE_KEY, at);
    if (resolution.reason === 'NOT_SET') {
        return { marketId, value: DEFAULT_PICKUP_CHANGE, reasonCode: 'PICKUP_CHANGE_DEFAULT', policyVersion: `${PICKUP_CHANGE_KEY}@default` };
    }
    if (resolution.scope === undefined || resolution.version === undefined || (resolution.scope !== 'country' && resolution.scope !== 'region' && resolution.scope !== 'market'))
        return strict;
    if (!validPickupChangeValue(resolution.value))
        return strict;
    const ref = settingScopeRefIn(resolution.scope, ctx) ?? country;
    const snap = settingSnapshotFields(relevant, PICKUP_CHANGE_KEY, resolution.scope, ref, resolution.version);
    return {
        marketId,
        value: copyValue(resolution.value),
        reasonCode: 'PICKUP_CHANGE_GOVERNED',
        policyVersion: pickupChangeVersionOf(resolution.scope, ref, resolution.version),
        ...(snap.contentHash !== undefined ? { contentHash: snap.contentHash } : {}),
        ...(snap.changeId !== undefined ? { changeId: snap.changeId } : {}),
    };
}
/**
 * Pure: whether the rider may move the pickup now under `value`, with the stable code of the answer. The order is the rider's reading order: the market's switch,
 * the phase (arrived and on board told apart), the count, then the distance.
 */
export function pickupChangeAllowed(ask, value) {
    if (!value.enabled || value.maxChanges === 0)
        return { allowed: false, code: 'PICKUP_CHANGE_DISABLED' };
    if (!value.allowedPhases.includes(ask.status)) {
        if (ask.status === 'arrived')
            return { allowed: false, code: 'PICKUP_CHANGE_DRIVER_ARRIVED' };
        if (ask.status === 'in_progress')
            return { allowed: false, code: 'PICKUP_CHANGE_ON_BOARD' };
        return { allowed: false, code: 'PICKUP_CHANGE_PHASE_NOT_ALLOWED' };
    }
    if (ask.changesSoFar >= value.maxChanges)
        return { allowed: false, code: 'PICKUP_CHANGE_LIMIT_REACHED' };
    if (ask.distanceFromAcceptedMeters !== undefined && (!Number.isFinite(ask.distanceFromAcceptedMeters) || ask.distanceFromAcceptedMeters > value.maxDistanceMeters)) {
        return { allowed: false, code: 'PICKUP_CHANGE_TOO_FAR' };
    }
    return { allowed: true, code: 'PICKUP_CHANGE_ALLOWED' };
}
/** Pure: whether a move of `moveMeters` (from the CURRENT pickup) is priced again under `value` - beyond the governed re-quote tolerance. */
export function pickupChangeNeedsRequote(moveMeters, value) {
    return !Number.isFinite(moveMeters) || moveMeters > value.requoteToleranceMeters;
}
export function pickupChangeWire(decision) {
    return { ...copyValue(decision.value), policyVersion: decision.policyVersion };
}
//# sourceMappingURL=ride-pickup-change-policy.js.map